import React from "react";
import type {TextHorizontal, TextVertical} from "../sponsorBooking";
import {TEXT_HORIZONTALS, TEXT_VERTICALS} from "../sponsorOptions";
import {
	HiddenControl,
	OptionError,
	OptionField,
	OptionHint,
	OptionLegend,
	PlacementCell,
	PlacementDot,
	PlacementGrid,
} from "./optionControls.styles";

/**
 * Where the advertiser's copy sits on their picture (kitecorp/transferify-ui#1868).
 *
 * Drawn as an actual 3x3 grid in the page's own 16:10 rather than as two dropdowns, because the control
 * IS a picture of the result: the cell you click is where your words go. Two axis selects would be a
 * correct control that makes the sponsor simulate the composition in their head.
 *
 * The nine cells are ONE radio group, so a keyboard user arrows through them and the browser announces
 * "3 of 9" rather than nothing. Each cell's accessible name is the pair in words ("Top, left"), which is
 * the only way a reader who cannot see the grid learns which corner they are on.
 *
 * The rows and columns come from `sponsorOptions.ts`'s exported value sets, not from a list retyped
 * here, so a placement the api stops accepting stops being offered in the same edit.
 *
 * Dumb by mandate (epic #26). The sender's own page-theme picker mounts it unchanged
 * (kitecorp/transferify-ui#1869).
 */
export interface TextPlacementFieldProps {
	label: string;
	hint: string;
	/** Unique on the page: nine radios share this name, and a second grid must not join the group. */
	name: string;
	value: {horizontal: TextHorizontal; vertical: TextVertical} | undefined;
	/** The translated name of one cell, vertical axis first. */
	cellName: (vertical: TextVertical, horizontal: TextHorizontal) => string;
	onChange: (placement: {horizontal: TextHorizontal; vertical: TextVertical}) => void;
	error: string | null;
}

export function TextPlacementField(
	{label, hint, name, value, cellName, onChange, error}: TextPlacementFieldProps,
): React.ReactElement {
	const errorId = `${name}-error`;
	return (
		<OptionField>
			<OptionLegend>{label}</OptionLegend>
			<OptionHint>{hint}</OptionHint>
			<PlacementGrid>
				{TEXT_VERTICALS.map(vertical => TEXT_HORIZONTALS.map(horizontal => {
					const selected = value?.vertical === vertical && value?.horizontal === horizontal;
					return (
						<PlacementCell key={`${vertical}-${horizontal}`} $selected={selected}>
							<HiddenControl
								type="radio"
								name={name}
								value={`${vertical}-${horizontal}`}
								checked={selected}
								// The cell shows a dot, which names nothing: without this the accessible
								// name would be empty and nine options would be indistinguishable.
								aria-label={cellName(vertical, horizontal)}
								aria-describedby={error === null ? undefined : errorId}
								onChange={() => onChange({horizontal, vertical})}
							/>
							<PlacementDot $selected={selected} aria-hidden="true"/>
						</PlacementCell>
					);
				}))}
			</PlacementGrid>
			{error !== null && <OptionError id={errorId} role="alert">{error}</OptionError>}
		</OptionField>
	);
}
